import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, addDoc, collection, Timestamp } from 'firebase/firestore';
import { db } from '../firebase/config';
import { COLLECTIONS } from '../firebase/collections';
import { useAuth } from '../context/AuthContext';
import { Calendar, Clock, User, Phone, Mail, MapPin, CheckCircle } from 'lucide-react';
import NavBar from '../components/NavBar';
import { notifyNewOrder } from '../services/notificationService';
import SmartImage from '../components/SmartImage';

const ServiceBooking = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [service, setService] = useState(null);
  const [formData, setFormData] = useState({ name: currentUser?.displayName || '', phone: '', email: currentUser?.email || '', address: '', bookingDate: '', bookingTime: '', notes: '' });
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [currentPage, setCurrentPage] = useState('services');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    if (!currentUser) { navigate('/login'); return; }
    loadService();
  }, [id, currentUser]);

  // التنقل عند تغيير الصفحة من NavBar
  useEffect(() => {
    if (currentPage === 'home') {
      navigate('/');
    } else if (currentPage === 'profile') {
      navigate('/profile');
    } else if (currentPage === 'maintenance') {
      navigate('/maintenance');
    } else if (currentPage === 'accessories') {
      navigate('/accessories');
    } else if (currentPage === 'parts') {
      navigate('/parts');
    } else if (currentPage === 'modifications') {
      navigate('/modifications');
    }
  }, [currentPage, navigate]);

  const loadService = async () => {
    try {
      const serviceDoc = await getDoc(doc(db, COLLECTIONS.SERVICES, id));
      if (serviceDoc.exists()) setService({ id: serviceDoc.id, ...serviceDoc.data() });
    } catch (error) { console.error('Error:', error); }
    finally { setLoading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const bookingData = { type: 'booking', serviceName: service.name, serviceId: service.id, customerName: formData.name, customerPhone: formData.phone, customerEmail: formData.email, customerAddress: formData.address, bookingDate: formData.bookingDate, bookingTime: formData.bookingTime, notes: formData.notes, status: 'pending', createdAt: Timestamp.now() };
      const docRef = await addDoc(collection(db, COLLECTIONS.ORDERS), bookingData);
      await notifyNewOrder({ ...bookingData, id: docRef.id });
      setSuccess(true);
      setTimeout(() => navigate('/profile'), 2000);
    } catch (error) { console.error('Error:', error); }
    finally { setSubmitting(false); }
  };

  if (!currentUser) return null;
  if (loading) return <div className="min-h-screen bg-gray-100"><NavBar currentPage={currentPage} setCurrentPage={setCurrentPage} mobileMenuOpen={mobileMenuOpen} setMobileMenuOpen={setMobileMenuOpen} /><div className="pt-24 flex items-center justify-center h-96"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500"></div></div></div>;
  if (!service) return null;

  return (
    <div className="min-h-screen bg-gray-100">
      <NavBar currentPage={currentPage} setCurrentPage={setCurrentPage} mobileMenuOpen={mobileMenuOpen} setMobileMenuOpen={setMobileMenuOpen} />
      <div className="pt-24 px-4 pb-12">
        <div className="max-w-4xl mx-auto">
          {success ? (
            <div className="bg-white rounded-2xl shadow-xl p-12 text-center"><CheckCircle className="w-24 h-24 text-green-500 mx-auto mb-6" /><h2 className="text-3xl font-bold text-gray-900 mb-4">تم حجز الخدمة بنجاح!</h2><p className="text-gray-600">سيتم التواصل معك قريباً لتأكيد الموعد</p></div>
          ) : (
            <div className="bg-white rounded-2xl shadow-xl p-8">
              <div className="bg-gradient-to-r from-yellow-500 to-orange-600 rounded-xl p-6 text-white mb-8">
                <div className="flex items-center gap-4">
                  <SmartImage
                    src={service.icon}
                    alt={service.name}
                    size="large"
                    fallback="🔧"
                  />
                  <div><h2 className="text-3xl font-bold">{service.name}</h2><p className="text-lg opacity-90">{service.price}</p></div>
                </div>
              </div>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div><label className="block text-sm font-medium text-gray-700 mb-2 text-right">الاسم الكامل</label><input type="text" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 text-right" /></div>
                  <div><label className="block text-sm font-medium text-gray-700 mb-2 text-right">رقم الهاتف</label><input type="tel" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} required className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 text-right" /></div>
                </div>
                <div><label className="block text-sm font-medium text-gray-700 mb-2 text-right">البريد الإلكتروني</label><input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} required className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 text-right" /></div>
                <div><label className="block text-sm font-medium text-gray-700 mb-2 text-right">العنوان</label><input type="text" value={formData.address} onChange={(e) => setFormData({ ...formData, address: e.target.value })} required className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 text-right" /></div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div><label className="block text-sm font-medium text-gray-700 mb-2 text-right">التاريخ المفضل</label><input type="date" value={formData.bookingDate} onChange={(e) => setFormData({ ...formData, bookingDate: e.target.value })} required className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500" /></div>
                  <div><label className="block text-sm font-medium text-gray-700 mb-2 text-right">الوقت المفضل</label><input type="time" value={formData.bookingTime} onChange={(e) => setFormData({ ...formData, bookingTime: e.target.value })} required className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500" /></div>
                </div>
                <div><label className="block text-sm font-medium text-gray-700 mb-2 text-right">ملاحظات إضافية</label><textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} rows="3" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 text-right" /></div>
                <button type="submit" disabled={submitting} className="w-full bg-gradient-to-r from-yellow-500 to-orange-600 hover:from-yellow-600 hover:to-orange-700 text-white font-bold py-4 rounded-lg transition-all disabled:opacity-50">
                  {submitting ? 'جاري الحجز...' : 'تأكيد الحجز'}
                </button>
              </form>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ServiceBooking;
